import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { getPictureList, getSignedUrl } from '../../server';
import { useMQTTSubscribe } from '../../hooks/useMqtt';
import { parseCameraFilename } from '../../lib/timestamp';

const POLL_INTERVAL_MS = 65_000;

// flask-server signs a URL for 15 minutes. A cached one is served up to
// SIGN_SERVE_MS old, and anything a sweep finds older than SIGN_REFRESH_MS is
// re-signed -- sweeping every SIGN_SWEEP_MS means a wanted URL is never more
// than about eleven minutes old, comfortably inside both limits.
const SIGN_LIFETIME_MS = 15 * 60_000;
const SIGN_SERVE_MS = SIGN_LIFETIME_MS - 60_000;
const SIGN_REFRESH_MS = 10 * 60_000;
const SIGN_SWEEP_MS = 60_000;

const EMPTY = [];

/**
 * Display-only port of weco-dashboard's Camera.js listing: files via
 * flask-server, refreshed whenever weco/<hostname>/camera announces a new
 * filename. `hostname` is the device that does the announcing -- the Pi
 * (denshi-camera), not the controller. Deliberately excludes Camera.js's
 * take_picture/take_video publish buttons -- this app never publishes.
 *
 * Unlike Camera.js (a flat gallery), this splits the list by the camera id
 * encoded in the filename and keeps every still and every video from each,
 * as two lists in capture order (oldest first, newest last), so a panel can
 * step back through a camera's history and the PHOTO/VIDEO switch flips
 * without re-listing. Time comes from the filename rather than blob
 * metadata -- it's the capture time, and it costs no extra request.
 *
 * Nothing here is signed: see useSignedUrls, which signs only the frames a
 * panel is actually about to show.
 *
 * @returns {{ cameras: { cameraId: number, photo: Frame[], video: Frame[] }[] }}
 *   where a Frame is { filename, cameraId, takenAt, isVideo }.
 */
export function useCameraFrames(enabled, mqttClient, hostname) {
  const [pictureList, setPictureList] = useState([]);

  // Re-listed on a timer as well as on the MQTT announcement below. The
  // announcement is faster when it arrives, but it is not something this app
  // can prove is arriving, and a feed that silently stops updating looks
  // identical to one where nothing is happening -- so the poll is what
  // actually guarantees the frame advances.
  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;

    async function refresh() {
      try {
        const list = await getPictureList();
        if (!cancelled) setPictureList(list || []);
      } catch (e) {
        console.error('Failed to get picture list', e);
      }
    }

    refresh();
    const timer = setInterval(refresh, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [enabled]);

  const onCameraMessage = useCallback((data) => {
    try {
      const payload = JSON.parse(data);
      const items = Array.isArray(payload) ? payload : [payload];
      const newFiles = items.map((i) => i.filename).filter(Boolean);

      if (newFiles.length > 0) {
        setPictureList((old) => [...newFiles.filter((f) => !old.includes(f)), ...old]);
      }
    } catch (e) {
      console.error('Failed to parse camera message', e);
    }
  }, []);

  useMQTTSubscribe(mqttClient, enabled && hostname ? `weco/${hostname}/camera` : null, onCameraMessage);

  // Ordered by the timestamp in each file's own name -- not by list order, so
  // a late-arriving MQTT announcement can't put an older frame on top. Kept
  // separately per kind so a fresh video never displaces the newest still,
  // or the other way round.
  const cameras = useMemo(() => {
    const byId = {};

    for (const filename of pictureList) {
      const parsed = parseCameraFilename(filename);
      if (!parsed) continue;

      const cam =
        byId[parsed.cameraId] ||
        (byId[parsed.cameraId] = { cameraId: parsed.cameraId, photo: [], video: [] });
      cam[parsed.isVideo ? 'video' : 'photo'].push({ filename, ...parsed });
    }

    const byTime = (a, b) => a.takenAt.getTime() - b.takenAt.getTime();
    return Object.values(byId)
      .map((cam) => ({ ...cam, photo: cam.photo.sort(byTime), video: cam.video.sort(byTime) }))
      .sort((a, b) => a.cameraId - b.cameraId);
  }, [pictureList]);

  return { cameras };
}

/**
 * Signed URLs for exactly the filenames asked for, fetched lazily and cached.
 * Signing is per frame on demand rather than over the whole listing, so
 * stepping through history costs one request per frame shown, not one per
 * frame that exists.
 *
 * If the camera stalls, the newest filename stops changing and the URL
 * behind it would quietly expire, leaving a broken frame exactly when someone
 * is watching to find out why -- so a sweep re-signs anything still wanted
 * once it is SIGN_REFRESH_MS old, and a cached URL past SIGN_SERVE_MS is
 * withheld rather than handed out broken.
 *
 * @param {(string | null | undefined)[]} filenames
 * @returns {Record<string, string>} filename -> url, for those that have one
 */
export function useSignedUrls(filenames, enabled = true) {
  // filename -> { url, at }. A ref rather than state so a sweep can read it
  // without re-running on every change; `version` re-renders consumers when
  // something new lands.
  const cacheRef = useRef(new Map());
  const inflightRef = useRef(new Set());
  const aliveRef = useRef(true);
  const [version, bump] = useReducer((n) => n + 1, 0);

  // A stable key, so the effects below run on a real change in what is
  // wanted rather than on every render that builds a fresh array.
  const wantedKey = useMemo(() => {
    const unique = new Set(filenames.filter(Boolean));
    return [...unique].sort().join('|');
  }, [filenames]);
  const wantedRef = useRef(EMPTY);
  wantedRef.current = wantedKey ? wantedKey.split('|') : EMPTY;

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const ensure = useCallback((names, olderThanMs) => {
    const now = Date.now();

    for (const filename of names) {
      const entry = cacheRef.current.get(filename);
      if (entry && now - entry.at < olderThanMs) continue;
      if (inflightRef.current.has(filename)) continue;

      inflightRef.current.add(filename);
      getSignedUrl(filename)
        .then((url) => {
          cacheRef.current.set(filename, { url, at: Date.now() });
          if (aliveRef.current) bump();
        })
        .catch((e) => console.error('Failed to get signed URL for', filename, e))
        .finally(() => inflightRef.current.delete(filename));
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    ensure(wantedRef.current, SIGN_REFRESH_MS);
  }, [enabled, wantedKey, ensure]);

  useEffect(() => {
    if (!enabled) return;

    const timer = setInterval(() => ensure(wantedRef.current, SIGN_REFRESH_MS), SIGN_SWEEP_MS);
    return () => clearInterval(timer);
  }, [enabled, ensure]);

  return useMemo(() => {
    const now = Date.now();
    const urls = {};
    for (const filename of wantedRef.current) {
      const entry = cacheRef.current.get(filename);
      if (entry && now - entry.at < SIGN_SERVE_MS) urls[filename] = entry.url;
    }
    return urls;
    // `version` is the cache's change signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantedKey, version]);
}
